import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { chatAPI, customerAPI } from '../../services/api';
import { useConfig } from '../../context/ConfigContext';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  Cog6ToothIcon,
  ClipboardIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

// Render inline markdown within a text fragment: converts **text** to
// <strong>text</strong>. Returns an array of strings and React elements
// suitable for spreading into JSX. Handles unmatched ** by leaving the
// remaining text as-is. Safe for any string — never throws.
function renderInlineMarkdown(text, keyPrefix) {
  if (!text || !text.includes('**')) return [text];
  const parts = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let idx = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={`${keyPrefix}-b${idx++}`} className="font-semibold text-gray-900">
        {match[1]}
      </strong>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

// Component to format assistant messages with bullet/numbered list support
function FormattedAssistantMessage({ content, isStreaming = false }) {
  const formatContent = (text) => {
    // Pre-process: if everything is on one line with bullets/bold, add newlines
    if (!text.includes('\n') && text.includes('**') && /[•●]/.test(text)) {
      text = text
        .replace(/\*\*([^*]+)\*\*/g, '\n\n$1\n')
        .replace(/[•●]/g, '\n•')
        .trim();
    }

    const sections = text.split('\n\n');
    const formatted = [];

    sections.forEach((section, sectionIndex) => {
      const lines = section.split('\n');
      const sectionElements = [];

      lines.forEach((line, lineIndex) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        // Bold headers: **Header:** or **Header**
        if ((trimmedLine.startsWith('**') && trimmedLine.endsWith(':**')) ||
            (trimmedLine.startsWith('**') && trimmedLine.endsWith('**'))) {
          const headerText = trimmedLine.replace(/\*\*/g, '');
          sectionElements.push(
            <div key={`${sectionIndex}-${lineIndex}`} className="font-semibold text-gray-900 mt-3 mb-2 first:mt-0">
              {headerText}
            </div>
          );
        // Plain header ending with colon
        } else if (trimmedLine.endsWith(':') && !/^[•●*-]/.test(trimmedLine) && !trimmedLine.match(/^\d+[\.\)]/)) {
          sectionElements.push(
            <div key={`${sectionIndex}-${lineIndex}`} className="font-semibold text-gray-900 mt-3 mb-2 first:mt-0">
              {trimmedLine}
            </div>
          );
        // Numbered list: 1. or 1) — respect user's ordered request
        } else if (trimmedLine.match(/^\d+[\.\)]\s/)) {
          const listText = trimmedLine.replace(/^\d+[\.\)]\s*/, '');
          const number = trimmedLine.match(/^(\d+)/)[1];
          const key = `${sectionIndex}-${lineIndex}`;
          sectionElements.push(
            <div key={key} className="flex items-start gap-2 py-1 ml-2">
              <span className="text-gray-500 font-medium min-w-[1.25rem] text-right">{number}.</span>
              <span className="text-gray-700 flex-1">{renderInlineMarkdown(listText, key)}</span>
            </div>
          );
        // Bullet: •, ●, *, or - at start of line → always render as bullet
        } else if (/^([•●]|[-*]\s)/.test(trimmedLine)) {
          const bulletText = trimmedLine.replace(/^([•●]|[-*]\s)\s*/, '').trim();
          const key = `${sectionIndex}-${lineIndex}`;
          sectionElements.push(
            <div key={key} className="flex items-start gap-2 py-1 ml-2">
              <span className="text-cleona-600 font-bold">•</span>
              <span className="text-gray-700 flex-1">{renderInlineMarkdown(bulletText, key)}</span>
            </div>
          );
        // Regular paragraph text
        } else {
          const key = `${sectionIndex}-${lineIndex}`;
          sectionElements.push(
            <div key={key} className="text-gray-700 leading-relaxed">
              {renderInlineMarkdown(trimmedLine, key)}
            </div>
          );
        }
      });

      if (sectionElements.length > 0) {
        formatted.push(
          <div key={sectionIndex} className={sectionIndex > 0 ? 'mt-3' : ''}>
            {sectionElements}
          </div>
        );
      }
    });

    return formatted;
  };

  return (
    <div className="space-y-1">
      {formatContent(content)}
      {isStreaming && (
        <span className="inline-block w-2 h-4 bg-gray-400 animate-pulse ml-1"></span>
      )}
    </div>
  );
}

// Action icons component
function MessageActions({ onCopy, onThumbUp, onThumbDown, onExport, onRefresh }) {
  return (
    <div className="flex items-center gap-2 mt-2">
      <button
        onClick={onCopy}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
        title="Copy"
      >
        <ClipboardIcon className="h-4 w-4" />
      </button>
      <button
        onClick={onThumbUp}
        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
        title="Good response"
      >
        <HandThumbUpIcon className="h-4 w-4" />
      </button>
      <button
        onClick={onThumbDown}
        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
        title="Bad response"
      >
        <HandThumbDownIcon className="h-4 w-4" />
      </button>
      <button
        onClick={onExport}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
        title="Export"
      >
        <ArrowUpTrayIcon className="h-4 w-4" />
      </button>
      <button
        onClick={onRefresh}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
        title="Regenerate"
      >
        <ArrowPathIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

// Typing indicator component
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 p-2">
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
  );
}

export default function Chat() {
  const [message, setMessage] = useState('');
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const { config } = useConfig();
  const { token } = useAuth();

  const { data: sessions } = useQuery('chatSessions', chatAPI.getChatSessions);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  const handleThumbUp = () => {
    toast.success('Thanks for your feedback!');
  };

  const handleThumbDown = () => {
    toast.success('Thanks for your feedback!');
  };

  const handleExport = (content) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chat-response.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRefresh = async (messageIndex) => {
    toast('Regenerating response...');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || isStreaming) return;

    const userMessage = {
      id: Date.now() + '-user',
      type: 'user',
      content: message.trim(),
      created_at: new Date().toISOString(),
    };

    const currentMessage = message.trim();
    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsStreaming(true);

    try {
      const assistantMessageId = Date.now() + '-assistant';
      const assistantMessage = {
        id: assistantMessageId,
        type: 'assistant',
        content: '',
        isStreaming: true,
        created_at: new Date().toISOString(),
        metadata: {
          sources: [],
          contact_info: {},
          categories: [],
          providers: []
        }
      };

      setMessages(prev => [...prev, assistantMessage]);
      setStreamingMessageId(assistantMessageId);

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const streamUrl = `${apiUrl}/api/chat/stream`;

      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: currentMessage,
          session_id: currentSession
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start streaming');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      // SSE events end with a blank line (\n\n). A single fetch read() may
      // contain a partial event, so we buffer across reads and only parse
      // events that have been fully received. Without this, large 'complete'
      // payloads get split mid-JSON, parse throws, and the UI never sees
      // the completion signal — leaving the cursor lingering.
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          if (buffer.trim()) {
            console.warn('SSE stream ended with unflushed buffer:', buffer.slice(0, 200));
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Split off complete events; the last segment is retained as a
        // partial that the next read() will continue.
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          // Each event can have multiple lines; only data: lines carry payloads.
          for (const line of event.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.substring(6));

              if (data.type === 'chunk') {
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: msg.content + data.content }
                    : msg
                ));
              } else if (data.type === 'complete') {
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMessageId
                    ? {
                        ...msg,
                        isStreaming: false,
                        metadata: {
                          sources: data.sources || [],
                          contact_info: data.contact_info || {},
                          categories: data.categories || [],
                          providers: data.providers || []
                        }
                      }
                    : msg
                ));

                if (data.session_id && !currentSession) {
                  setCurrentSession(data.session_id);
                }
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', parseError);
            }
          }
        }
      }

    } catch (error) {
      console.error('Streaming chat error:', error);

      setMessages(prev => prev.filter(msg => msg.id !== streamingMessageId));

      const errorMessage = {
        id: Date.now() + '-error',
        type: 'assistant',
        content: 'Sorry, I encountered an error while processing your message. Please try again.',
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to send message');
    } finally {
      setIsStreaming(false);
      setStreamingMessageId(null);
    }
  };

  const startNewSession = () => {
    setCurrentSession(null);
    setMessages([]);
  };

  const loadSession = async (sessionId) => {
    try {
      const session = await chatAPI.getChatSession(sessionId);
      setCurrentSession(sessionId);
      setMessages(session.messages);
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 60) return '1 Month ago';
    return `${Math.floor(diffDays / 30)} Months ago`;
  };

  // Group messages by date
  const getDateSeparator = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] font-source">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white rounded-xl border border-gray-100 mr-6">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h1 className="text-xl font-bold text-gray-900 font-arsenal">
              Cleona Smart Assistant™
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Test your Smart Assistant™ and manage its settings
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={startNewSession}
              className="flex items-center gap-2 px-4 py-2.5 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors text-sm font-medium"
            >
              <PlusIcon className="h-4 w-4" />
              New Chat
            </button>
            <Link
              to="/admin/profile"
              title="Smart Assistant™ Settings"
              className="p-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors inline-flex items-center justify-center"
            >
              <Cog6ToothIcon className="h-5 w-5 text-gray-600" />
            </Link>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-cleona-50 rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-cleona-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Start a Conversation
                </h3>
                <p className="text-gray-500 text-sm max-w-sm">
                  Ask questions about your uploaded documents and test how your assistant responds.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Date Separator */}
              {messages.length > 0 && (
                <div className="flex items-center justify-center">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="px-4 text-sm text-gray-400">
                    {getDateSeparator(messages[0].created_at)}
                  </span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
              )}

              {messages.map((msg, index) => (
                <div key={msg.id}>
                  {msg.type === 'user' ? (
                    // User message - shaded bubble
                    <div className="flex justify-start">
                      <div className="max-w-2xl">
                        <div className="bg-gray-100 rounded-xl px-5 py-4">
                          <p className="text-gray-900 text-sm">
                            {msg.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Assistant message - no background, clean text
                    <div className="flex justify-start">
                      <div className="max-w-2xl">
                        <div className="px-5 py-4">
                          {msg.isStreaming && !msg.content ? (
                            <TypingIndicator />
                          ) : (
                            <div className="text-sm leading-relaxed">
                              <FormattedAssistantMessage content={msg.content} isStreaming={msg.isStreaming} />
                            </div>
                          )}
                        </div>
                        {!msg.isStreaming && (
                          <div className="px-5">
                            <MessageActions
                              onCopy={() => handleCopy(msg.content)}
                              onThumbUp={handleThumbUp}
                              onThumbDown={handleThumbDown}
                              onExport={() => handleExport(msg.content)}
                              onRefresh={() => handleRefresh(index)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isStreaming && messages[messages.length - 1]?.type === 'user' && (
                <div className="flex justify-start">
                  <TypingIndicator />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="px-6 py-4 border-t border-gray-100">
          <form onSubmit={handleSubmit}>
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask anything"
                className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400"
                disabled={isStreaming}
              />
            </div>
          </form>
        </div>
      </div>

      {/* Right Sidebar - Recent Sessions */}
      <div className="w-72 flex-shrink-0">
        <div className="bg-white rounded-xl border border-gray-100 p-4 h-full">
          <h2 className="text-lg font-semibold text-gray-900 font-arsenal mb-4">
            Recent Sessions
          </h2>

          <div className="space-y-1">
            {sessions?.map((session) => (
              <button
                key={session.session_id}
                onClick={() => loadSession(session.session_id)}
                className={`w-full text-left px-3 py-3 rounded-lg transition-colors ${
                  currentSession === session.session_id
                    ? 'bg-cleona-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 text-sm">
                    {session.user_name || `Session ${session.session_id.slice(0, 6)}`}
                  </span>
                  <span className="text-xs text-cleona-600">
                    {formatRelativeTime(session.created_at || session.last_activity)}
                  </span>
                </div>
                <p className="text-xs text-cleona-600 mt-0.5">
                  {session.message_count || session.messages?.length || 0} messages
                </p>
              </button>
            )) || (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">No sessions yet</p>
                <p className="text-xs text-gray-400 mt-1">Start a chat to create your first session</p>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
